import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  TextField,
  FormLabel,
  IconButton,
  Divider,
  Stack,
  Box,
  Avatar,
  Paper,
  Chip,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import ImageIcon from '@mui/icons-material/Image';
import { useFormik } from 'formik';
import { restaurantSchema } from 'schema';
import { getApi, postApi, patchApi } from 'views/services/api';
import { toast } from 'react-toastify';

const AddItems = (props) => {
  const { open, handleClose, currentItem } = props;
  const [currentImg, setCurrentImg] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  const initialValues = {
    itemName: '',
    itemImage: '',
    amount: '',
    category: '',
    inventoryItem: null
  };

  const [inventoryList, setInventoryList] = useState([]);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const res = await getApi(`api/inventory/view/${hotel?.hotelId}`);
        if(res.status === 200) {
          setInventoryList(res.data.items || []);
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (open) {
      fetchInventory();
    }
  }, [open]);

  const AddData = async (values, resetForm) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (key === 'itemImage') {
        formData.append('itemImage', values.itemImage);
      } else {
        formData.append(key, values[key]);
      }
    });

    try {
      let response;
      if (currentItem) {
        response = await patchApi(`api/restaurant/edit/${currentItem._id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await postApi('api/restaurant/add', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      if (response.status === 200) {
        toast.success(currentItem ? 'Item actualizado exitosamente' : 'Item agregado exitosamente');
        handleClose();
        resetForm();
        setImagePreview(null);
        setCurrentImg(null);
      } else {
        toast.error('No se puede procesar el item');
      }
    } catch (error) {
      console.error('Error al procesar item:', error);
      toast.error('Error al procesar el item');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: restaurantSchema,
    onSubmit: async (values, { resetForm }) => {
      values.hotelId = JSON.parse(localStorage.getItem('hotelData')).hotelId;
      AddData(values, resetForm);
    }
  });

  useEffect(() => {
    if (currentItem) {
      formik.setValues({
        itemName: currentItem.itemName || '',
        amount: currentItem.amount || '',
        category: currentItem.category || '',
        inventoryItem: currentItem.inventoryItem || null
      });
      setCurrentImg(currentItem.itemImage);
      setImagePreview(null);
    } else {
      formik.resetForm();
      setCurrentImg(null);
      setImagePreview(null);
    }
  }, [currentItem, open]);

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue('itemImage', file);
      setCurrentImg(null);

      // Crear preview de la imagen
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDialogClose = () => {
    formik.resetForm();
    setImagePreview(null);
    setCurrentImg(null);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: currentItem ? 'warning.main' : 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {currentItem ? <EditIcon sx={{ color: 'white', fontSize: 24 }} /> : <AddIcon sx={{ color: 'white', fontSize: 24 }} />}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {currentItem ? 'Editar Item' : 'Agregar Nuevo Item'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentItem ? 'Actualiza la información del item' : 'Completa los datos del nuevo item del menú'}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleDialogClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'error.lighter',
                color: 'error.main'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Categoría e Item Name */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Información Básica
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Categoría</FormLabel>
              <TextField
                id="category"
                name="category"
                size="small"
                fullWidth
                placeholder="Ej: Entradas, Platos Fuertes, Bebidas"
                value={formik.values.category}
                onChange={formik.handleChange}
                error={formik.touched.category && Boolean(formik.errors.category)}
                helperText={formik.touched.category && formik.errors.category}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Nombre del Item</FormLabel>
              <TextField
                id="itemName"
                name="itemName"
                size="small"
                fullWidth
                placeholder="Ej: Pizza Margarita, Hamburguesa Clásica"
                value={formik.values.itemName}
                onChange={formik.handleChange}
                error={formik.touched.itemName && Boolean(formik.errors.itemName)}
                helperText={formik.touched.itemName && formik.errors.itemName}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Precio */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Precio</FormLabel>
              <TextField
                id="amount"
                name="amount"
                size="small"
                type="number"
                fullWidth
                placeholder={`Precio en ${currency}`}
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Enlazar a Inventario */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Enlazar a Inventario (Opcional)</FormLabel>
              <Autocomplete
                size="small"
                fullWidth
                options={inventoryList}
                getOptionLabel={(option) => option ? `${option.itemName} (${option.stockQuantity} ${option.unitMeasure})` : ''}
                value={inventoryList.find(i => i._id === formik.values.inventoryItem) || null}
                onChange={(e, newValue) => {
                  formik.setFieldValue('inventoryItem', newValue ? newValue._id : null);
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Busca un insumo..." 
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>

            {/* Imagen */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, display: 'block', fontWeight: 500 }}>Imagen del Item</FormLabel>
              <Button
                component="label"
                variant="outlined"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderRadius: 2,
                  py: 0.75,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderWidth: 2
                  }
                }}
              >
                {formik.values.itemImage ? 'Cambiar Imagen' : 'Subir Imagen'}
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </Button>
            </Grid>

            {/* Preview de imagen */}
            {(imagePreview || currentImg) && (
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                    Vista Previa de la Imagen
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      src={imagePreview || `${process.env.REACT_APP_BASE_URL}api/uploads/restaurant/Item/${currentImg}`}
                      variant="rounded"
                      sx={{
                        width: 100,
                        height: 100,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        boxShadow: 2
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Box>
                      {formik.values.itemImage && formik.values.itemImage.name ? (
                        <>
                          <Typography variant="body2" fontWeight={600}>
                            Archivo seleccionado:
                          </Typography>
                          <Chip label={formik.values.itemImage.name} size="small" color="success" sx={{ mt: 0.5 }} />
                        </>
                      ) : currentImg ? (
                        <>
                          <Typography variant="body2" fontWeight={600}>
                            Imagen actual:
                          </Typography>
                          <Chip label={currentImg} size="small" color="primary" sx={{ mt: 0.5 }} />
                        </>
                      ) : null}
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            )}

            {/* Información adicional */}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'primary.lighter',
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderRadius: 2
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FastfoodIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="caption" color="text.secondary">
                    <strong>Tip:</strong> Asegúrate de usar imágenes de alta calidad para una mejor presentación en el menú.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleDialogClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.secondary',
              bgcolor: 'action.hover'
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color={currentItem ? 'warning' : 'primary'}
          sx={{
            borderRadius: 2,
            px: 4,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4
            }
          }}
        >
          {currentItem ? 'Actualizar Item' : 'Agregar Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddItems.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  currentItem: PropTypes.object
};

export default AddItems;
